import type {
    Chance,
    Compare,
    Entry,
    Location,
    Props,
    Scope,
    Target,
    TargetSelect,
    Trigger,
    YieldChange
} from './Props';

type LabelOption = { value: number; label: string };

type DescriptionContext = {
    withFormat: boolean;
    propNameMap: Map<string, string>;
    tagLabelMap: Map<number, string>;
    rarityLabelMap: Map<number, string>;
};

type EntryHandlerMap = {
    [K in Entry['type']]: (entry: Extract<Entry, { type: K }>, props: Props, context: DescriptionContext) => string;
};

type TriggerHandlerMap = {
    [K in Trigger['type']]: (trigger: Extract<Trigger, { type: K }>, props: Props, context: DescriptionContext) => string;
};

type TargetSelectHandlerMap = {
    [K in TargetSelect['field']]: (target: Extract<TargetSelect, { field: K }>, context: DescriptionContext) => string;
};

type ReplaceTargetSelect = TargetSelect & { keepTag?: boolean };

type ValueYieldChange = Extract<YieldChange, { value: number }>;
type RandomYieldChange = Extract<YieldChange, { min: number }>;

type YieldChangeHandlerMap = {
    add: (yieldChange: ValueYieldChange) => string;
    multiply: (yieldChange: ValueYieldChange) => string;
    random: (yieldChange: RandomYieldChange) => string;
};

type EntryTriggerOverrideMap = {
    [K in Entry['type']]?: Partial<{
        [T in Trigger['type']]: (entry: Extract<Entry, { type: K }>, props: Props, context: DescriptionContext) => string;
    }>;
};

const ENTRY_SEPARATOR = '。';
const INVALID_TRIGGER_TARGET = '[目标为触发者时触发器类型必须为目标查找]';

const targetDescMap: Record<Target['target'], string> = {
    self: '自身',
    triggers: '{triggers}',
    select: '{select}'
};

const scopeDescMap: Record<Scope['scope'], string> = {
    neighbor: '相邻',
    row: '同一行',
    column: '同一列',
    diagonal: '对角线上的',
    global: ''
};

const locationDescMap: Record<NonNullable<Location['location']>, string> = {
    battle: '',
    depot: '到材料库',
    corner: '到角落',
    empty: '到空位置'
};

const locatedDescMap: Record<NonNullable<Location['location']>, string> = {
    battle: '位于战场时',
    depot: '位于材料库时',
    corner: '位于角落时',
    empty: '位于空位置时'
};

const compareSourceMap: Record<NonNullable<Compare['source']>, string> = {
    none: '',
    yield: '魔力产出'
};

const compareTermMap: Record<NonNullable<Compare['term']>, string> = {
    equal: '等于',
    greaterOrEqual: '点或以上',
    lessOrEqual: '点或以下',
    greater: '点以上',
    less: '点以下'
};

const compareSymbolMap: Record<NonNullable<Compare['term']>, string> = {
    equal: '等于',
    greaterOrEqual: '≥',
    lessOrEqual: '≤',
    greater: '>',
    less: '<'
};

export const createPropDescriptionContext = (
    all: Props[],
    tags: LabelOption[],
    rarities: LabelOption[],
    withFormat = false
): DescriptionContext => ({
    withFormat,
    propNameMap: new Map(all.map(prop => [prop._id.toString(), prop.name])),
    tagLabelMap: new Map(tags.map(tag => [tag.value, tag.label])),
    rarityLabelMap: new Map(rarities.map(rarity => [rarity.value, rarity.label]))
});

export const getPropDescription = (
    props: Props,
    all: Props[],
    tags: LabelOption[],
    rarities: LabelOption[],
    withFormat = false
): string | undefined => {
    const context = createPropDescriptionContext(all, tags, rarities, withFormat);
    return describePropsEntries(props, context);
};

export const getDescription = getPropDescription;

export const describePropsEntries = (
    props: Props,
    context: DescriptionContext
): string | undefined => {
    if (!props?.entries?.length) return '';

    const parts: string[] = [];
    let nextEntryPrefix = '';
    let nextEntryAfterPrev = false;
    let previousEntry: Entry | undefined;
    for (let i = 0; i < props.entries.length; i++) {
        const entry = props.entries[i];
        if (shouldSkipEntry(props.entries, i)) continue;

        if (entry.type === 'Next') {
            nextEntryPrefix += getTriggeredActionPrefix(entry, props, context);
            nextEntryAfterPrev ||= entry.trigger.type === 'AfterPrev';
            continue;
        }

        if (entry.trigger.type === 'EachTriggered' && parts.length > 0) {
            parts.push(`，${nextEntryPrefix}${getEachTriggeredContinuationText(entry, previousEntry, props, context)}`);
            nextEntryPrefix = '';
            nextEntryAfterPrev = false;
            previousEntry = entry;
            continue;
        }

        const text = describeEntry(entry, props, context);
        if (!text) continue;

        if (parts.length > 0 && entry.trigger.type !== 'AfterPrev' && !nextEntryAfterPrev) {
            parts.push(ENTRY_SEPARATOR);
        }
        parts.push(`${nextEntryPrefix}${text}`);
        nextEntryPrefix = '';
        nextEntryAfterPrev = false;
        previousEntry = entry;
    }

    return parts.length ? `${parts.join('')}${ENTRY_SEPARATOR}` : '';
};

export const describeEntry = (
    entry: Entry,
    props: Props,
    context: DescriptionContext
): string => {
    const overrideGroup = entryTriggerOverrides[entry.type as keyof EntryTriggerOverrideMap] as
        | Partial<Record<Trigger['type'], (entry: Entry, props: Props, context: DescriptionContext) => string>>
        | undefined;
    const override = overrideGroup?.[entry.trigger.type];
    if (override) {
        return override(entry, props, context);
    }
    return (entryHandlers as Record<Entry['type'], (entry: Entry, props: Props, context: DescriptionContext) => string>)[entry.type](entry, props, context);
};

export const describeTrigger = (
    trigger: Trigger,
    props: Props,
    context: DescriptionContext
): string => {
    if (!trigger || (trigger.type === 'EachTurns' && trigger.count <= 1)) return '';
    return triggerHandlers[trigger.type](trigger as never, props, context);
};

export const describeTargetSelect = (
    target: TargetSelect,
    context: DescriptionContext
): string => targetSelectHandlers[target.field](target as never, context);

export const describeAddTargetSelect = (
    target: TargetSelect,
    context: DescriptionContext
): string => addTargetSelectHandlers[target.field](target as never, context);

export const describeReplaceTargetSelect = (
    target: TargetSelect,
    context: DescriptionContext
): string => replaceTargetSelectHandlers[target.field](target as never, context);

export const describeGrowTargetSelect = (
    target: TargetSelect,
    context: DescriptionContext
): string => growTargetSelectHandlers[target.field](target as never, context);

const describePrefixedTargetSelect = (
    target: TargetSelect,
    context: DescriptionContext,
    prefix: string
) => describeTargetSelect(target, context).replace(getLimitDesc(target.limit), `${getLimitDesc(target.limit)}${prefix}`);

export const getChanceDesc = (chance?: Chance['chance']) =>
    chance && chance < 100 ? `${chance}%概率` : '';

export const getLocationDesc = (location?: Location['location']) => {
    if (location === undefined) return '';
    if (location in locationDescMap) return locationDescMap[location];
    throw new Error(`未知的位置类型: ${String(location)}`);
};

export const getExtraYieldDesc = (yieldChange: YieldChange) =>
    yieldChangeHandlers[yieldChange.method](yieldChange as never);

export function joinItems(items?: Array<string | undefined>, sep = '、', lastSep = '或'): string {
    const filtered = items?.filter(isNonEmpty) ?? [];
    if (filtered.length > 1) {
        return filtered.slice(0, -1).join(sep) + lastSep + filtered[filtered.length - 1];
    }
    return filtered[0] ?? '';
}

const hasInvalidTriggerTarget = (entry: Entry): boolean =>
    'target' in entry && entry.target === 'triggers' && entry.trigger.type !== 'RelativeProp';

const getEntryTrigger = (entry: Entry, props: Props, context: DescriptionContext) =>
    describeTrigger(entry.trigger, props, context);

const getExtraYieldTargetSelect = (
    entry: Extract<Entry, { type: 'ExtraYield', target: 'select' }>,
    context: DescriptionContext
) => {
    if (entry.scope === 'global' && entry.limit && entry.limit > 0) {
        const targetWithoutLimit: TargetSelect = { ...entry, limit: 0 };
        const subject = applyTokens(describeTargetSelect(targetWithoutLimit, context), { scope: '' });
        return `随机${entry.limit}个${subject}`;
    }
    if (entry.scope === 'global' && !entry.limit) {
        return applyTokens(describeTargetSelect(entry, context), { scope: '全场' });
    }
    return describeTargetSelect(entry, context);
};

const getRemoveTargetSelect = (entry: Extract<Entry, { type: 'Remove', target: 'select' }>, context: DescriptionContext) =>
    entry.limit && entry.limit > 0
        ? describePrefixedTargetSelect(entry, context, '随机')
        : describeTargetSelect(entry, context);

const getReplaceTargetSelect = (
    entry: Extract<Entry, { type: 'Replace' }>,
    props: Props,
    context: DescriptionContext
) => {
    if (entry.keepTag && entry.field === 'rarity') {
        const tagLabels = props.tags
            ?.map(tag => context.tagLabelMap.get(tag))
            .filter(isNonEmpty)
            .map(label => maybeFormat(label, '#d97706', context));
        if (tagLabels?.length) {
            const rarityLabels = entry.rarities
                ?.map(rarity => context.rarityLabelMap.get(rarity))
                .filter(isNonEmpty)
                .map(label => maybeFormat(label, '#8b5cf6', context));
            if (rarityLabels?.length) {
                return buildReplaceTargetSelectText(
                    { ...entry, keepTag: false },
                    `随机${joinItems(rarityLabels, '、', getTargetSelectLastSep(entry))}${joinItems(tagLabels, '、', '和')}`
                );
            }
        }
    }
    return describeReplaceTargetSelect(entry, context);
};

const getGrowTargetSelect = (entry: Extract<Entry, { type: 'Grow', target: 'select' }>, context: DescriptionContext) => {
    if (entry.scope === 'global' && entry.limit && entry.limit > 0) {
        const targetWithoutLimit: TargetSelect = { ...entry, limit: 0 };
        const subject = applyTokens(describeGrowTargetSelect(targetWithoutLimit, context), { scope: '' });
        return `随机${entry.limit}个${subject}`;
    }
    return describeGrowTargetSelect(entry, context);
};

const getAddTargetSelect = (entry: TargetSelect, context: DescriptionContext) =>
    describeAddTargetSelect(entry, context);

const getTimerTargetSelect = (entry: Extract<Entry, { type: 'Timer', target: 'select' }>, context: DescriptionContext) => {
    if (entry.scope === 'global' && entry.limit && entry.limit > 0) {
        const targetWithoutLimit: TargetSelect = { ...entry, limit: 0 };
        const subject = applyTokens(describeTargetSelect(targetWithoutLimit, context), { scope: '' });
        return `场上随机${entry.limit}个${subject}`;
    }
    return describeTargetSelect(entry, context);
};

const getTriggeredTargetDesc = (
    trigger: Extract<Trigger, { type: 'RelativeProp' }>,
    context: DescriptionContext
) => {
    if (trigger.scope === 'global') {
        if (trigger.field === 'same') {
            return trigger.limit && trigger.limit > 0
                ? `场上数量≥${trigger.limit}的材料`
                : '场上材料';
        }
        if (trigger.field === 'any') {
            if (hasCompareCondition(trigger)) {
                return `${getLimitDesc(trigger.limit)}场上${getCompactCompareDesc(trigger)}的${getRelativePropPlainSubjectText(trigger, context)}`;
            }
            return `${getLimitDesc(trigger.limit)}全场${getRelativePropSubjectText(trigger, context)}`;
        }
        return `${getLimitDesc(trigger.limit)}${getRelativePropSubjectText(trigger, context)}`;
    }
    if (trigger.field === 'same') {
        return `${getLimitDesc(trigger.limit)}${scopeDescMap[trigger.scope]}的相同材料`;
    }
    return applyTokens(describeTargetSelect(trigger, context), {
        scope: scopeDescMap[trigger.scope]
    });
};

const getRelativePropObjectText = (
    trigger: Extract<Trigger, { type: 'RelativeProp' }>,
    context: DescriptionContext
) => {
    if (trigger.scope === 'global') {
        if (trigger.field === 'same') {
            return trigger.limit && trigger.limit > 0
                ? `场上数量≥${trigger.limit}的材料`
                : '场上材料';
        }
        if (trigger.limit && trigger.limit > 0) {
            return `场上随机${trigger.limit}个${getRelativePropSubjectText(trigger, context)}`;
        }
        return `${getRelativePropSubjectText(trigger, context)}`;
    }
    if (trigger.field === 'same') {
        const scopeText = scopeDescMap[trigger.scope];
        const limitText = getLimitDesc(trigger.limit);
        return `${limitText}${scopeText}的相同材料`;
    }
    return `${getLimitDesc(trigger.limit)}${scopeDescMap[trigger.scope]}${getRelativePropSubjectText(trigger, context)}`;
};

const getRelativePropSubjectText = (
    trigger: Extract<Trigger, { type: 'RelativeProp' }>,
    context: DescriptionContext
) => {
    const triggerWithoutLimit: TargetSelect = { ...trigger, limit: 0 };
    return applyTokens(describeTargetSelect(triggerWithoutLimit, context), {
        scope: ''
    });
};

const getRelativePropPlainSubjectText = (
    trigger: Extract<Trigger, { type: 'RelativeProp' }>,
    context: DescriptionContext
) => {
    const triggerWithoutLimitAndCompare: TargetSelect = {
        ...trigger,
        limit: 0,
        source: 'none',
        term: undefined,
        dest: undefined
    };
    return applyTokens(describeTargetSelect(triggerWithoutLimitAndCompare, context), {
        scope: ''
    });
};

const getRelativePropConditionText = (
    trigger: Extract<Trigger, { type: 'RelativeProp' }>,
    context: DescriptionContext
) => {
    if (trigger.scope === 'global' && trigger.field === 'same') {
        return `场上每有一种材料数量≥${getRelativePropThresholdText(trigger.limit)}`;
    }
    if (hasCompareCondition(trigger)) {
        return `${trigger.scope === 'global' ? '场上有' : getLocalRelativePropPrefix(trigger.scope)}${getRelativePropPlainSubjectText(trigger, context)}${getCompactCompareDesc(trigger)}时`;
    }
    const subject = getRelativePropSubjectText(trigger, context);
    if (trigger.scope === 'global') {
        if (trigger.limit === -1) {
            return `场上没有${subject}`;
        }
        return `场上每有${getRelativePropCountText(trigger.limit)}${subject}`;
    }
    return getLocalRelativePropConditionText(trigger, subject);
};

const getRelativePropConnector = (trigger: Extract<Trigger, { type: 'RelativeProp' }>) =>
    hasCompareCondition(trigger) ? '' : trigger.scope === 'global' && trigger.limit !== -1 ? '就' : '时';

const getExtraYieldRelativePropConnector = (trigger: Extract<Trigger, { type: 'RelativeProp' }>) =>
    trigger.scope === 'global' && trigger.limit !== -1 ? '' : getRelativePropConnector(trigger);

const getTargetBeenRemovedConditionText = (
    trigger: Extract<Trigger, { type: 'TargetBeenRemoved' }>,
    context: DescriptionContext
) => applyTokens(`${describeTargetSelect(trigger, context)}被消除时`, {
    scope: trigger.scope === 'global' ? '场上' : scopeDescMap[trigger.scope]
});

const getAddRelativePropConditionText = (
    trigger: Extract<Trigger, { type: 'RelativeProp' }>,
    context: DescriptionContext
) => {
    if (trigger.scope === 'global') {
        const limitText = trigger.limit && trigger.limit > 1 ? `至少${trigger.limit}个` : '';
        return `场上有${limitText}${getRelativePropSubjectText(trigger, context)}时`;
    }
    return `${getLocalRelativePropPrefix(trigger.scope)}${getRelativePropSubjectText(trigger, context)}时`;
};

const getLocalRelativePropPrefix = (scope: Exclude<Scope['scope'], 'global'>) => {
    switch (scope) {
        case 'neighbor':
            return '有相邻';
        case 'row':
            return '同一行有';
        case 'column':
            return '同一列有';
        case 'diagonal':
            return '对角线上有';
    }
};

const getLocalRelativePropConditionText = (
    trigger: Extract<Trigger, { type: 'RelativeProp' }>,
    subject: string
) => {
    if (trigger.scope === 'global') return '';
    if (!trigger.limit || trigger.limit <= 1) {
        return `${getLocalRelativePropPrefix(trigger.scope)}${subject}`;
    }
    switch (trigger.scope) {
        case 'neighbor':
            return `有至少${trigger.limit}个相邻${subject}`;
        case 'row':
            return `同一行有至少${trigger.limit}个${subject}`;
        case 'column':
            return `同一列有至少${trigger.limit}个${subject}`;
        case 'diagonal':
            return `对角线上有至少${trigger.limit}个${subject}`;
    }
};

const buildCommonActionText = (
    entry: Extract<Entry, { chance: number }>,
    props: Props,
    context: DescriptionContext,
    actionText: string
) => `${getEntryTrigger(entry, props, context)}${getChanceDesc(entry.chance)}${actionText}`;

const getTriggeredActionPrefix = (
    entry: Entry,
    props: Props,
    context: DescriptionContext
) => {
    const trigger = getEntryTrigger(entry, props, context);
    if (!trigger) return '';
    if (
        entry.trigger.type === 'AfterPrev'
        || entry.trigger.type === 'BeenRemoved'
        || entry.trigger.type === 'BeenReplaced'
        || entry.trigger.type === 'BeenUsed'
        || entry.trigger.type === 'EachTurns'
        || entry.trigger.type === 'EachRemove'
        || entry.trigger.type === 'EachTriggered'
        || entry.trigger.type === 'Located'
        || entry.trigger.type === 'TargetBeenRemoved'
    ) {
        return trigger;
    }
    return `${trigger}时`;
};

const getEachTriggeredContinuationText = (
    entry: Entry,
    previousEntry: Entry | undefined,
    props: Props,
    context: DescriptionContext
) => {
    const triggerText = getTriggeredActionPrefix(entry, props, context);
    const text = describeEntry(entry, props, context);
    const actionText = triggerText && text.startsWith(triggerText)
        ? text.slice(triggerText.length)
        : text;
    const previousAction = previousEntry ? getEntryActionName(previousEntry) : '';
    return `${previousAction ? `每次${previousAction}` : triggerText}${actionText}`;
};

const getEntryActionName = (entry: Entry): string => {
    switch (entry.type) {
        case 'Remove':
            return '消除';
        case 'Add':
            return '生成';
        case 'Replace':
            return '转化';
        case 'ExtraYield':
            return '产出';
        case 'Grow':
            return '永久增加';
        case 'Collect':
            return '收集';
        case 'Gain':
            return '获取';
        case 'RemoveEntry':
            return '净化';
        case 'DrawWeightChange':
            return '调整抽取概率';
        case 'Protect':
            return '保护';
        case 'Refine':
            return '调和';
        case 'Transfer':
            return '转移';
        case 'Next':
            return '触发下一词条';
        case 'Timer':
            return '倒计时';
    }
};

const getEntryExtraYieldDesc = (
    entry: Extract<Entry, { type: 'ExtraYield' }>,
    props: Props
) => {
    if (entry.trigger.type === 'EachRemove' && entry.method === 'add' && entry.value > 0) {
        return `产出${entry.value}点魔力`;
    }
    if ((props.type === 2 || props.type === 3) && entry.target === 'self' && entry.method === 'add' && entry.value > 0) {
        return `产出${entry.value}点魔力`;
    }
    return getExtraYieldDesc(entry);
};

const getGrowDesc = (count: number) =>
    `永久${count < 0 ? '减少' : '增加'}${Math.abs(count)}点魔力产出`;

const getTimerDesc = (count: number) =>
    `倒计时${count > 0 ? '+' : ''}${count}`;

const entryHandlers: EntryHandlerMap = {
    Remove: (entry, props, context) => {
        if (hasInvalidTriggerTarget(entry)) return INVALID_TRIGGER_TARGET;
        if (entry.target === 'triggers' && entry.trigger.type === 'RelativeProp') {
            return `${getChanceDesc(entry.chance)}${formatVerb('消除', '#f00', context)}${getTriggeredTargetDesc(entry.trigger, context)}`;
        }
        const trigger = getEntryTrigger(entry, props, context);
        return applyTokens(
            `${entry.target === 'triggers' ? '' : trigger}${getChanceDesc(entry.chance)}${formatVerb('消除', '#f00', context)}${targetDescMap[entry.target]}`,
            {
                triggers: trigger,
                select: entry.target === 'select'
                    ? applyTokens(getRemoveTargetSelect(entry, context), {
                        scope: entry.scope === 'global' ? '' : scopeDescMap[entry.scope]
                    })
                    : ''
            }
        );
    },
    Add: (entry, props, context) =>
        applyTokens(
            `${buildCommonActionText(entry, props, context, formatVerb('生成', '#0a0', context))}${getAddTargetSelect(entry, context)}${getLocationDesc(entry.location)}`,
            { scope: '' }
        ),
    Replace: (entry, props, context) => {
        if (hasInvalidTriggerTarget(entry)) return INVALID_TRIGGER_TARGET;
        return applyTokens(
            `${entry.target === 'self' ? '' : '使'}${buildCommonActionText(entry, props, context, formatVerb('转化', '#0aa', context))}为${getReplaceTargetSelect(entry, props, context)}`,
            { scope: '' }
        );
    },
    ExtraYield: (entry, props, context) => {
        if (hasInvalidTriggerTarget(entry)) return INVALID_TRIGGER_TARGET;
        const trigger = getEntryTrigger(entry, props, context);
        if (entry.target === 'triggers') {
            if (entry.trigger.type === 'RelativeProp') {
                return `使${getTriggeredTargetDesc(entry.trigger, context)}${getChanceDesc(entry.chance)}${getEntryExtraYieldDesc(entry, props)}`;
            }
            return applyTokens(`使${targetDescMap[entry.target]}${getChanceDesc(entry.chance)}${getEntryExtraYieldDesc(entry, props)}`, {
                triggers: trigger
            });
        }
        return applyTokens(
            `${getTriggeredActionPrefix(entry, props, context)}${entry.target === 'self' ? '' : targetDescMap[entry.target]}${getChanceDesc(entry.chance)}${getEntryExtraYieldDesc(entry, props)}`,
            {
                select: entry.target === 'select' ? getExtraYieldTargetSelect(entry, context) : '',
                scope: entry.target === 'select' ? scopeDescMap[entry.scope] : ''
            }
        );
    },
    Grow: (entry, props, context) =>
        applyTokens(
            `${getTriggeredActionPrefix(entry, props, context)}${entry.target === 'self' ? '' : targetDescMap[entry.target]}${getGrowDesc(entry.count)}`,
            {
                triggers: getEntryTrigger(entry, props, context),
                select: entry.target === 'select' ? getGrowTargetSelect(entry, context) : '',
                scope: entry.target === 'select' ? (entry.scope === 'global' ? '全场' : scopeDescMap[entry.scope]) : ''
            }
        ),
    Collect: (entry, props, context) =>
        applyTokens(
            `${getEntryTrigger(entry, props, context)}从${describeTargetSelect(entry, context)}收集${entry.count}点魔力${entry.times > 0 ? `，可收集${entry.times}次` : ''}`,
            { scope: entry.scope === 'global' ? '全场' : scopeDescMap[entry.scope] }
        ),
    Gain: (entry, props, context) =>
        `${getEntryTrigger(entry, props, context)}获取收集的魔力`,
    Refine: (entry, props, context) =>
        `${getEntryTrigger(entry, props, context)}调和次数+${entry.count}`,
    Timer: (entry, props, context) => {
        const target = entry.target ?? 'self';
        if (target === 'self') {
            return `${getEntryTrigger(entry, props, context)}${getTimerDesc(entry.count)}`;
        }
        return applyTokens(
            `${getTriggeredActionPrefix(entry, props, context)}${targetDescMap[target]}${getTimerDesc(entry.count)}`,
            {
                triggers: getEntryTrigger(entry, props, context),
                select: entry.target === 'select' ? getTimerTargetSelect(entry, context) : '',
                scope: entry.target === 'select' ? (entry.scope === 'global' ? '全场' : scopeDescMap[entry.scope]) : ''
            }
        );
    },
    Transfer: (entry, props, context) =>
        `${getEntryTrigger(entry, props, context)}转移位置`,
    Next: () => '',
    RemoveEntry: (entry, props, context) => {
        const indexDesc = entry.indexes?.length ? `第${entry.indexes.map(i => i + 1).join('、')}条` : '';
        return applyTokens(
            `${getEntryTrigger(entry, props, context)}${formatVerb('净化', '#8b6b00', context)}${describeTargetSelect(entry, context)}${indexDesc ? `的${indexDesc}特性` : ''}`,
            { scope: '' }
        );
    },
    DrawWeightChange: (entry, props, context) => {
        const labels = entry.raritiesToChange
            .map(value => context.rarityLabelMap.get(value))
            .filter(isNonEmpty);
        return `${getEntryTrigger(entry, props, context)}抽到${joinItems(labels, '、', '和')}物品的概率乘以${entry.weightMultiplier}`;
    },
    Protect: (entry, props, context) =>
        applyTokens(
            `${getEntryTrigger(entry, props, context)}保护${describeTargetSelect(entry, context)}不被消除`,
            { scope: entry.scope === 'global' ? '全场' : scopeDescMap[entry.scope] }
        ),
};

const entryTriggerOverrides: EntryTriggerOverrideMap = {
    Add: {
        RelativeProp: (entry, _props, context) =>
            applyTokens(
                `${getAddRelativePropConditionText(entry.trigger as Extract<Trigger, { type: 'RelativeProp' }>, context)}${getChanceDesc(entry.chance)}${formatVerb('生成', '#0a0', context)}${getAddTargetSelect(entry, context)}${getLocationDesc(entry.location)}`,
                { scope: '' }
            )
    },
    Remove: {
        AfterPrev: (entry, props, context) => {
            if (hasInvalidTriggerTarget(entry)) return INVALID_TRIGGER_TARGET;
            const targetText = targetDescMap[entry.target];
            return applyTokens(`并${formatVerb('消除', '#f00', context)}${targetText}`, {
                triggers: getEntryTrigger(entry, props, context)
            });
        },
        RelativeProp: (entry, props, context) => {
            if (hasInvalidTriggerTarget(entry)) return INVALID_TRIGGER_TARGET;
            if (entry.target !== 'self') {
                return entryHandlers.Remove(entry, props, context);
            }
            const relativeTrigger = entry.trigger as Extract<Trigger, { type: 'RelativeProp' }>;
            return `${getRelativePropConditionText(relativeTrigger, context)}时${getChanceDesc(entry.chance)}${formatVerb('消除', '#f00', context)}自身`;
        }
    },
    Replace: {
        RelativeProp: (entry, props, context) => {
            if (hasInvalidTriggerTarget(entry)) return INVALID_TRIGGER_TARGET;
            const relativeTrigger = entry.trigger as Extract<Trigger, { type: 'RelativeProp' }>;
            if (entry.target === 'triggers') {
                return applyTokens(
                    `使${getRelativePropObjectText(relativeTrigger, context)}${getChanceDesc(entry.chance)}${formatVerb('转化', '#0aa', context)}为${getReplaceTargetSelect(entry, props, context)}`,
                    { scope: '' }
                );
            }
            if (entry.target !== 'self') {
                return entryHandlers.Replace(entry, props, context);
            }
            const chanceText = getChanceDesc(entry.chance);
            return applyTokens(
                `${getRelativePropConditionText(relativeTrigger, context)}${getRelativePropConnector(relativeTrigger)}${chanceText ? `有${chanceText}` : ''}${formatVerb('转化', '#0aa', context)}为${getReplaceTargetSelect(entry, props, context)}`,
                { scope: '' }
            );
        }
    },
    ExtraYield: {
        RelativeProp: (entry, props, context) => {
            if (hasInvalidTriggerTarget(entry)) return INVALID_TRIGGER_TARGET;
            if (entry.target === 'triggers') {
                return entryHandlers.ExtraYield(entry, props, context);
            }
            const relativeTrigger = entry.trigger as Extract<Trigger, { type: 'RelativeProp' }>;
            return applyTokens(
                `${getRelativePropConditionText(relativeTrigger, context)}${getExtraYieldRelativePropConnector(relativeTrigger)}${entry.target === 'self' ? '' : targetDescMap[entry.target]}${getChanceDesc(entry.chance)}${getEntryExtraYieldDesc(entry, props)}`,
                {
                    select: entry.target === 'select' ? getExtraYieldTargetSelect(entry, context) : '',
                    scope: entry.target === 'select' ? scopeDescMap[entry.scope] : ''
                }
            );
        }
    },
    Grow: {
        RelativeProp: (entry, props, context) => {
            if (hasInvalidTriggerTarget(entry)) return INVALID_TRIGGER_TARGET;
            const relativeTrigger = entry.trigger as Extract<Trigger, { type: 'RelativeProp' }>;
            if (entry.target === 'triggers') {
                return `使${getRelativePropObjectText(relativeTrigger, context)}${getGrowDesc(entry.count)}`;
            }
            return entryHandlers.Grow(entry, props, context);
        }
    },
    Timer: {
        RelativeProp: (entry, props, context) => {
            const target = entry.target ?? 'self';
            const relativeTrigger = entry.trigger as Extract<Trigger, { type: 'RelativeProp' }>;
            if (target === 'triggers') {
                return `使${getRelativePropObjectText(relativeTrigger, context)}${getTimerDesc(entry.count)}`;
            }
            if (target === 'self') {
                return `${getRelativePropConditionText(relativeTrigger, context)}${getRelativePropConnector(relativeTrigger)}${getTimerDesc(entry.count)}`;
            }
            return entryHandlers.Timer(entry, props, context);
        }
    },
    Transfer: {
        RelativeProp: (entry, _props, context) => {
            const relativeTrigger = entry.trigger as Extract<Trigger, { type: 'RelativeProp' }>;
            return `与${getRelativePropObjectText(relativeTrigger, context)}交换位置`;
        }
    }
};

const triggerHandlers: TriggerHandlerMap = {
    EachTurns: trigger => {
        if (!trigger.loops) return `每${trigger.count}轮`;
        if (trigger.loops === 1) return `${trigger.count}轮后`;
        return `每${trigger.count}轮行动，循环${trigger.loops}次`;
    },
    AfterPrev: () => '，然后',
    EachRemove: () => '每消除一个',
    EachTriggered: () => '上一词条每次触发时',
    BeenRemoved: () => '被消除时',
    BeenReplaced: () => '转化时',
    RelativeProp: (trigger, _props, context) => getRelativePropConditionText(trigger, context),
    BeenUsed: trigger => `${trigger.count > 1 ? `可使用${trigger.count}次，` : ''}使用后`,
    Located: trigger => trigger.location ? locatedDescMap[trigger.location] : '',
    TargetBeenRemoved: (trigger, _props, context) => getTargetBeenRemovedConditionText(trigger, context)
};

const targetSelectHandlers: TargetSelectHandlerMap = {
    prop: (target, context) => {
        if (!target.props?.length) return '[选择至少1个目标道具]';
        const names = target.props
            .map(propId => formatPropName(propId, context))
            .filter(isNonEmpty);
        return buildTargetSelectText(target, joinItems(names, '、', getTargetSelectLastSep(target)));
    },
    tag: (target, context) => {
        if (!target.tags?.length) return '[选择至少1个道具标签]';
        const labels = target.tags
            .map(tag => context.tagLabelMap.get(tag))
            .filter(isNonEmpty)
            .map(label => maybeFormat(label, '#d97706', context));
        return buildTargetSelectText(target, joinItems(labels, '、', getTargetSelectLastSep(target)));
    },
    rarity: (target, context) => {
        if (!target.rarities?.length) return '[选择至少1个道具品质]';
        const labels = target.rarities
            .map(rarity => context.rarityLabelMap.get(rarity))
            .filter(isNonEmpty)
            .map(label => maybeFormat(label, '#8b5cf6', context));
        return buildTargetSelectText(target, `${joinItems(labels, '、', getTargetSelectLastSep(target))}${maybeFormat('材料', '#8b5cf6', context)}`);
    },
    any: target => buildTargetSelectText(target, '材料'),
    same: target => buildTargetSelectText(target, '相同材料')
};

const addTargetSelectHandlers: TargetSelectHandlerMap = {
    ...targetSelectHandlers,
    tag: (target, context) => {
        if (!target.tags?.length) return '[选择至少1个道具标签]';
        const labels = target.tags
            .map(tag => context.tagLabelMap.get(tag))
            .filter(isNonEmpty)
            .map(label => maybeFormat(label, '#d97706', context));
        return buildTargetSelectText(target, `随机${joinItems(labels, '、', getTargetSelectLastSep(target))}`);
    },
    rarity: (target, context) => {
        if (!target.rarities?.length) return '[选择至少1个道具品质]';
        const labels = target.rarities
            .map(rarity => context.rarityLabelMap.get(rarity))
            .filter(isNonEmpty)
            .map(label => maybeFormat(label, '#8b5cf6', context));
        return buildTargetSelectText(target, `随机${joinItems(labels, '、', getTargetSelectLastSep(target))}${maybeFormat('材料', '#8b5cf6', context)}`);
    }
};

const replaceTargetSelectHandlers: TargetSelectHandlerMap = {
    prop: (target, context) => {
        if (!target.props?.length) return '[选择至少1个目标道具]';
        const names = target.props
            .map(propId => formatPropName(propId, context))
            .filter(isNonEmpty);
        return buildReplaceTargetSelectText(target, joinItems(names, '、', getTargetSelectLastSep(target)));
    },
    tag: (target, context) => {
        if (!target.tags?.length) return '[选择至少1个道具标签]';
        const labels = target.tags
            .map(tag => context.tagLabelMap.get(tag))
            .filter(isNonEmpty)
            .map(label => maybeFormat(label, '#d97706', context));
        return buildReplaceTargetSelectText(target, `随机${joinItems(labels, '、', getTargetSelectLastSep(target))}`);
    },
    rarity: (target, context) => {
        if (!target.rarities?.length) return '[选择至少1个道具品质]';
        const labels = target.rarities
            .map(rarity => context.rarityLabelMap.get(rarity))
            .filter(isNonEmpty)
            .map(label => maybeFormat(label, '#8b5cf6', context));
        return buildReplaceTargetSelectText(target, `随机${joinItems(labels, '、', getTargetSelectLastSep(target))}${maybeFormat('材料', '#8b5cf6', context)}`);
    },
    any: target => buildReplaceTargetSelectText(target, '材料'),
    same: target => buildReplaceTargetSelectText(target, '相同材料')
};

const growTargetSelectHandlers: TargetSelectHandlerMap = {
    prop: (target, context) => {
        if (!target.props?.length) return '[选择至少1个目标道具]';
        const names = target.props
            .map(propId => formatPropName(propId, context))
            .filter(isNonEmpty);
        return buildTargetSelectText(target, joinItems(names, '、', getTargetSelectLastSep(target)));
    },
    tag: (target, context) => {
        if (!target.tags?.length) return '[选择至少1个道具标签]';
        const labels = target.tags
            .map(tag => context.tagLabelMap.get(tag))
            .filter(isNonEmpty)
            .map(label => maybeFormat(label, '#d97706', context));
        return buildTargetSelectText(target, joinItems(labels, '、', getTargetSelectLastSep(target)));
    },
    rarity: (target, context) => {
        if (!target.rarities?.length) return '[选择至少1个道具品质]';
        const labels = target.rarities
            .map(rarity => context.rarityLabelMap.get(rarity))
            .filter(isNonEmpty)
            .map(label => maybeFormat(label, '#8b5cf6', context));
        return buildTargetSelectText(target, `${joinItems(labels, '、', getTargetSelectLastSep(target))}${maybeFormat('材料', '#8b5cf6', context)}`);
    },
    any: target => buildTargetSelectText(target, '材料'),
    same: target => buildTargetSelectText(target, '相同材料')
};

const yieldChangeHandlers: YieldChangeHandlerMap = {
    add: yieldChange => `${yieldChange.value < 0 ? '减少' : '额外产出'}${Math.abs(yieldChange.value)}点魔力`,
    multiply: yieldChange => `额外产出${yieldChange.value}倍魔力`,
    random: yieldChange => `随机产出${yieldChange.min}-${yieldChange.max}点魔力`
};

const getLimitDesc = (limit?: number): string => {
    if (!limit) return '';
    return limit === -1 ? '没有' : `${limit}个`;
};

const getRelativePropCountText = (limit?: number): string => {
    if (!limit) return '1个';
    return limit === -1 ? '没有' : `${limit}个`;
};

const getRelativePropThresholdText = (limit?: number): string => {
    if (!limit) return '1';
    return `${limit}`;
};

const getCompareDesc = (target: Compare): string => {
    if (!target.source || target.source === 'none') return '';
    if (target.dest === undefined || !target.term) return '';
    return `具有${target.dest}${compareTermMap[target.term]}${compareSourceMap[target.source]}时`;
};

const getCompactCompareDesc = (target: Compare): string => {
    if (!target.source || target.source === 'none') return '';
    if (target.dest === undefined || !target.term) return '';
    return `${compareSourceMap[target.source]}${compareSymbolMap[target.term]}${target.dest}`;
};

const hasCompareCondition = (target: Compare): boolean =>
    Boolean(target.source && target.source !== 'none' && target.term && target.dest !== undefined);

const maybeFormat = (text: string, color: string, context: DescriptionContext) =>
    context.withFormat ? `<span style="color: ${color}">${text}</span>` : text;

const formatPropName = (propId: string, context: DescriptionContext) => {
    const name = context.propNameMap.get(propId);
    if (!name) return undefined;
    if (!context.withFormat) return name;
    return `<span data-prop-id="${escapeHtmlAttribute(propId)}" style="color: #179fdb; cursor: help">${escapeHtml(name)}</span>`;
};

const formatVerb = (text: string, color: string, context: DescriptionContext) =>
    maybeFormat(text, color, context);

const escapeHtml = (text: string) =>
    text
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');

const escapeHtmlAttribute = escapeHtml;

const getTargetSelectLastSep = (target: TargetSelect): '或' | '和' =>
    target.logic === 'and' ? '和' : '或';

const buildTargetSelectText = (target: TargetSelect, subject: string) =>
    `${getLimitDesc(target.limit)}{scope}${subject}${getCompareDesc(target)}`;

const buildReplaceTargetSelectText = (target: ReplaceTargetSelect, subject: string) =>
    `${getReplaceLimitDesc(target.limit)}{scope}${target.keepTag ? '同系' : ''}${subject}${getCompareDesc(target)}`;

const getReplaceLimitDesc = (limit?: number): string => {
    if (!limit || limit === 1) return '';
    return limit === -1 ? '没有' : `${limit}个`;
};

const shouldSkipEntry = (entries: Entry[], index: number): boolean => {
    if (index <= 0) return false;
    const entry = entries[index];
    const prev = entries[index - 1];
    return entry.type === 'Remove'
        && entry.target === 'self'
        && entry.trigger.type === 'AfterPrev'
        && prev.type === 'Remove'
        && prev.target === 'triggers'
        && prev.trigger.type === 'RelativeProp'
        && prev.trigger.scope === 'global';
};

const applyTokens = (template: string, tokens: Partial<Record<'triggers' | 'select' | 'scope', string>>) =>
    Object.entries(tokens).reduce((result, [key, value]) => result.replaceAll(`{${key}}`, value ?? ''), template);

const isNonEmpty = (value: string | undefined): value is string => Boolean(value);
